import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { withDemoFlag } from '../demoMode';
import { toast } from 'sonner';
import { translateError } from '@/crm/utils/translateError';
import { Paperclip, Upload, Trash2, Download, FileText, Image as ImageIcon, File } from 'lucide-react';
import { useConfirm } from './ConfirmDialog';

interface Props {
  entityType: 'prestation' | 'devis' | 'facture' | 'client' | 'artiste';
  entityId: string;
  compact?: boolean;
}

interface PieceJointe {
  id: string;
  file_path: string;
  file_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
}

const formatSize = (b: number | null) => {
  if (!b) return '';
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
  return `${(b / 1024 / 1024).toFixed(1)} Mo`;
};

const iconFor = (mime: string | null) => {
  if (!mime) return File;
  if (mime.startsWith('image/')) return ImageIcon;
  if (mime.includes('pdf') || mime.includes('text') || mime.includes('document')) return FileText;
  return File;
};

export default function PiecesJointes({ entityType, entityId, compact }: Props) {
  const [items, setItems] = useState<PieceJointe[]>([]);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    if (!entityId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('pieces_jointes')
      .select('*')
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('created_at', { ascending: false });
    if (!error) setItems((data || []) as PieceJointe[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, [entityType, entityId]);

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} : trop volumineux (max 25 Mo)`);
          continue;
        }
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${entityType}/${entityId}/${Date.now()}_${safeName}`;
        const { error: upErr } = await supabase.storage.from('ged').upload(path, file, { upsert: false });
        if (upErr) { toast.error(`Upload ${file.name} : ${translateError(upErr.message)}`); continue; }
        const { error: dbErr } = await supabase.from('pieces_jointes').insert(withDemoFlag({
          entity_type: entityType,
          entity_id: entityId,
          file_path: path,
          file_name: file.name,
          mime_type: file.type || null,
          size_bytes: file.size,
        }));
        if (dbErr) {
          await supabase.storage.from('ged').remove([path]);
          toast.error(`Enregistrement ${file.name} : ${translateError(dbErr.message)}`);
        }
      }
      toast.success('Fichiers ajoutés');
      await load();
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const download = async (pj: PieceJointe) => {
    const { data, error } = await supabase.storage.from('ged').createSignedUrl(pj.file_path, 60);
    if (error || !data?.signedUrl) { toast.error('Téléchargement impossible'); return; }
    window.open(data.signedUrl, '_blank');
  };

  const remove = async (pj: PieceJointe) => {
    const ok = await confirm({
      title: 'Supprimer ce fichier ?',
      description: `« ${pj.file_name} »`,
      confirmLabel: 'Supprimer',
      destructive: true,
    });
    if (!ok) return;
    await supabase.storage.from('ged').remove([pj.file_path]);
    const { error } = await supabase.from('pieces_jointes').delete().eq('id', pj.id);
    if (error) toast.error(translateError(error.message));
    else { toast.success('Pièce supprimée'); load(); }
  };

  return (
    <div className={compact ? '' : 'mt-4'}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-2">
          <Paperclip size={13} /> Pièces jointes {items.length > 0 && <span className="text-amber-400">({items.length})</span>}
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || !entityId}
          className="px-3 py-1.5 text-xs bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded flex items-center gap-1.5 disabled:opacity-50"
        >
          <Upload size={12} /> {uploading ? 'Envoi…' : 'Ajouter'}
        </button>
        <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
      </div>

      {loading ? (
        <div className="text-xs text-slate-500">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-slate-500 italic px-3 py-3 border border-dashed border-slate-800 rounded text-center">
          Aucune pièce jointe. Glissez vos contrats, devis signés, photos de l'événement…
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((pj) => {
            const Icon = iconFor(pj.mime_type);
            return (
              <div key={pj.id} className="flex items-center gap-2 px-3 py-2 bg-slate-900/60 rounded border border-slate-800 text-sm">
                <Icon size={14} className="text-slate-400 shrink-0" />
                <button onClick={() => download(pj)} className="flex-1 min-w-0 text-left truncate text-slate-200 hover:text-amber-400" title={pj.file_name}>
                  {pj.file_name}
                </button>
                <span className="text-[10px] text-slate-500 shrink-0">{formatSize(pj.size_bytes)}</span>
                <button onClick={() => download(pj)} className="text-slate-400 hover:text-amber-400" title="Télécharger">
                  <Download size={13} />
                </button>
                <button onClick={() => remove(pj)} className="text-slate-400 hover:text-red-400" title="Supprimer">
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
